import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { environment } from '../../../environments/environment';

export interface GeneralLedgerOption {
  id: string;
  name: string;
}

/** Full detail model for a single general ledger account, used by the editable screen. */
export interface GeneralLedgerAccountDetail {
  generalLedgerAccountId: string;
  generalLedgerAccount: string;
  generalLedgerAccountName: string;
  generalLedgerId: string;
  generalLedgerName: string;
  generalLedgerGroupId: string | null;
  generalLedgerGroupName: string;
  kindOfAccount: string;
  bookNegativeValuesIndicator: boolean;
  bookByEmployment: boolean;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memoValue: string;
}

export interface GeneralLedgerAccountListItem {
  generalLedgerAccountId: string;
  generalLedgerId: string;
  generalLedger_Account: string;
  generalLedger_Account_Name: string;
  generalLedger_Name: string;
  generalLedger_Group_ID: string | null;
  generalLedger_Group_Name: string;
  kindOf_Account: string;
  bookNegativeValuesIndicator: boolean;
  bookByEmployment: boolean;
  startDate: string | Date | null;
  endDate: string | Date | null;
}

interface GeneralLedgerAccountApiModel {
  generalLedger_Account_ID: string;
  generalLedger_Account_Name: string;
  generalLedger_Account: string;
  generalLedger_ID: string;
  generalLedger_Group_ID: string | null;
  bookByEmployment: boolean;
  kindOf_Account: string;
  bookNegativeValuesIndicator: boolean;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value?: string | null;
  generalLedger_Group_Name?: string | null;
  generalLedger_Name?: string | null;
}

interface GeneralLedgerAccountApiPayload {
  generalLedger_Account_ID: string;
  generalLedger_Account_Name: string;
  generalLedger_Account: string;
  generalLedger_ID: string;
  generalLedger_Group_ID: string | null;
  bookByEmployment: boolean;
  display_EmploymentNumber: boolean;
  display_EmploymentName: boolean;
  kindOf_Account: string;
  bookNegativeValuesIndicator: boolean;
  startDate: string | null;
  endDate: string | null;
  changeUser: string;
  memo_Value: string;
}

interface GeneralLedgerApiModel {
  generalLedger_ID: string;
  generalLedger_Name: string;
}

interface AddGeneralLedgerAccountResponse {
  data?: string;
  generalLedger_Account_ID?: string;
  generalLedgerAccountId?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class GeneralLedgerAccountService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/GeneralLedgerAccount';
  private generalLedgerUrl = environment.apiUrl + '/kbj-frontend/api/v1/GeneralLedger';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all general ledger accounts for the given general ledger.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getByContext(generalLedgerId: string): Observable<GeneralLedgerAccountListItem[]> {
    return this.http
      .get<GeneralLedgerAccountApiModel[] | null>(
        this.apiUrl + '/ByContext/' + generalLedgerId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiModelToListItem(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the general ledgers for the current context, used to populate the
   * "Grootboek" dropdown.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getGeneralLedgersByContext(): Observable<GeneralLedgerOption[]> {
    return this.http
      .get<GeneralLedgerApiModel[] | null>(this.generalLedgerUrl + '/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => ({
            id: item.generalLedger_ID,
            name: item.generalLedger_Name,
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single general ledger account by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns null when the API responds with 404 or 204 No Content.
   */
  getById(id: string): Observable<GeneralLedgerAccountDetail | null> {
    return this.http
      .get<GeneralLedgerAccountApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiModelToDetail(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new general ledger account. The account id is always forced to the
   * empty guid so the backend can generate a new one.
   */
  add(account: GeneralLedgerAccountDetail): Observable<GeneralLedgerAccountDetail> {
    const payload = this.mapDetailToApiPayload({
      ...account,
      generalLedgerAccountId: EMPTY_GUID,
    });

    return this.http
      .post<AddGeneralLedgerAccountResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({
          ...account,
          generalLedgerAccountId: this.getCreatedAccountId(response),
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing general ledger account.
   */
  update(id: string, account: GeneralLedgerAccountDetail): Observable<GeneralLedgerAccountDetail> {
    const payload = this.mapDetailToApiPayload({
      ...account,
      generalLedgerAccountId: id,
    });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...account, generalLedgerAccountId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Removes a general ledger account by its id.
   */
  removeGeneralLedgerAccount(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  // ------------------------------------------------------------------------------
  // Helper methods for mapping between API payloads and the list model.

  private mapApiModelToListItem(
    apiModel: GeneralLedgerAccountApiModel,
  ): GeneralLedgerAccountListItem {
    return {
      generalLedgerAccountId: apiModel.generalLedger_Account_ID,
      generalLedgerId: apiModel.generalLedger_ID,
      generalLedger_Account: apiModel.generalLedger_Account,
      generalLedger_Account_Name: apiModel.generalLedger_Account_Name,
      generalLedger_Name: apiModel.generalLedger_Name ?? '',
      generalLedger_Group_ID: apiModel.generalLedger_Group_ID,
      generalLedger_Group_Name: apiModel.generalLedger_Group_Name ?? '',
      kindOf_Account: apiModel.kindOf_Account,
      bookNegativeValuesIndicator: apiModel.bookNegativeValuesIndicator,
      bookByEmployment: apiModel.bookByEmployment,
      startDate: apiModel.startDate,
      endDate: apiModel.endDate,
    };
  }

  private mapApiModelToDetail(apiModel: GeneralLedgerAccountApiModel): GeneralLedgerAccountDetail {
    return {
      generalLedgerAccountId: apiModel.generalLedger_Account_ID,
      generalLedgerAccount: apiModel.generalLedger_Account,
      generalLedgerAccountName: apiModel.generalLedger_Account_Name,
      generalLedgerId: apiModel.generalLedger_ID,
      generalLedgerName: apiModel.generalLedger_Name ?? '',
      generalLedgerGroupId: apiModel.generalLedger_Group_ID ?? null,
      generalLedgerGroupName: apiModel.generalLedger_Group_Name ?? '',
      kindOfAccount: apiModel.kindOf_Account,
      bookNegativeValuesIndicator: apiModel.bookNegativeValuesIndicator,
      bookByEmployment: apiModel.bookByEmployment,
      startDate: apiModel.startDate,
      endDate: apiModel.endDate,
      memoValue: apiModel.memo_Value ?? '',
    };
  }

  private mapDetailToApiPayload(detail: GeneralLedgerAccountDetail): GeneralLedgerAccountApiPayload {
    return {
      generalLedger_Account_ID: detail.generalLedgerAccountId,
      generalLedger_Account_Name: detail.generalLedgerAccountName,
      generalLedger_Account: detail.generalLedgerAccount,
      generalLedger_ID: detail.generalLedgerId,
      generalLedger_Group_ID: detail.generalLedgerGroupId || null,
      bookByEmployment: detail.bookByEmployment,
      display_EmploymentNumber: false,
      display_EmploymentName: false,
      kindOf_Account: detail.kindOfAccount,
      bookNegativeValuesIndicator: detail.bookNegativeValuesIndicator,
      startDate: this.formatApiDate(detail.startDate),
      endDate: this.formatApiDate(detail.endDate),
      changeUser: EMPTY_GUID,
      memo_Value: detail.memoValue ?? '',
    };
  }

  private getCreatedAccountId(response: AddGeneralLedgerAccountResponse | null): string {
    return (
      response?.data ??
      response?.generalLedger_Account_ID ??
      response?.generalLedgerAccountId ??
      EMPTY_GUID
    );
  }

  private formatApiDate(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
